import { NgFor, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, Component, HostBinding, Inject, signal } from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { IconButtonComponent } from "app/core/ui";
import { FormButtonComponent } from "app/core/ui/form-button-component";
import { PointButtonDirective } from "app/core/ui/point-button-directive";
import { ScoreFormStore } from "../score-form-store";
import { ScoreFormEntries } from "../score-form.model";

import { APP_CONFIG, AppConfig } from "app/core";
import { Observable, of, tap, timer } from "rxjs";
import { ScoreFormPart1Component } from "../score-form-part1-component";
import { ScoreFormPart2Component } from "../score-form-part2-component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "fof-score-form",
  templateUrl: "./template.html",
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    NgIf,
    NgFor,
    FormButtonComponent,
    IconButtonComponent,
    PointButtonDirective,
    ScoreFormPart1Component,
    ScoreFormPart2Component,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/** A component displaying a form to obtain user information (name, email, employer etc..) */
export class ScoreFormComponent {
  scoreFormPart1 = new FormGroup({
    description: new FormControl("", Validators.required),
    file: new FormControl<File | null>(null),
    isEmployee: new FormControl(false, Validators.required),
  });
  scoreFormPart2 = new FormGroup({
    lastname: new FormControl("", Validators.required),
    firstname: new FormControl("", Validators.required),
    email: new FormControl("", [Validators.required, Validators.email]),
    userId: new FormControl(""),
    company: new FormControl(""),
  });

  scoreForm = new FormGroup({
    part1: this.scoreFormPart1,
    part2: this.scoreFormPart2,
  });

  protected get isEmployee(): boolean {
    return this.scoreFormPart1.value.isEmployee ?? false;
  }

  protected activeStepId = 0;

  protected readonly availableForAria = signal<boolean>(false);

  /** Styles to be applied to previous and next buttons */
  protected buttonsStyles = {
    color: "white",
    bgColor: "var(--fof-primary-color)",
    borderColor: "var(--fof-primary-color)",
    colorOnHover: "var(--fof-primary-color)",
    bgColorOnHover: "white",
    borderColorOnHover: "var(--fof-primary-color)",
  };

  @HostBinding("class") hostClasses = "d-flex flex-column justify-content-between h-100";

  constructor(
    private scoreFormStore: ScoreFormStore,
    @Inject(APP_CONFIG) private appConfig: AppConfig
  ) {
    this.makeAvailableForAria();
    this.onIsEmployeeChange().pipe(takeUntilDestroyed()).subscribe();
  }

  onSubmit() {
    if (this.scoreForm.invalid) {
      return;
    }
    const result = {
      ...this.scoreForm.value.part1,
      ...this.scoreForm.value.part2,
    };
    this.scoreFormStore.publish(result as ScoreFormEntries);
  }

  onCancel() {
    this.scoreForm.reset();
    this.scoreFormStore.cancelRequest();
  }

  activateStep(index: number) {
    this.activeStepId = index;
    this.availableForAria.set(false);
    this.makeAvailableForAria();
  }

  private makeAvailableForAria() {
    timer(this.appConfig.currentBlockAriaVisibleAfter).subscribe(() => {
      this.availableForAria.set(true);
    });
  }

  /**
   * When the isEmployee checkbox value changes, update the validators of
   * userId and company fields and reset their values
   */
  private onIsEmployeeChange(): Observable<boolean | null> {
    return (
      this.scoreFormPart1.get("isEmployee")?.valueChanges.pipe(
        tap((isEmployee) => {
          if (isEmployee) {
            this.scoreFormPart2.get("userId")?.setValidators([Validators.required]);
            this.scoreFormPart2.get("company")?.setValidators([Validators.required]);
          } else {
            this.scoreFormPart2.get("userId")?.setValue("");
            this.scoreFormPart2.get("company")?.setValue("");
            this.scoreFormPart2.get("userId")?.clearValidators();
            this.scoreFormPart2.get("company")?.clearValidators();
          }
        })
      ) ?? of(null)
    );
  }
}
